// The home app at the site's own addresses (its route is the path: /, /templates, ./paths.ts) and /apps/:app — one app
// of the project: the app runtime's frame, full-bleed, exactly as cortex's preview frames it (apps/web pages/apps/Apps.tsx
// without cortex's own header: that bar is cortex's, for sharing the app; the app has none). The shared speed dial
// floats over it. A loader until the app says it mounted (postMessage `cortex-app` ready), a failure state with Try
// again, and the app's own route mirrored in the address both ways (the home app's as the path, another's in the
// query).
import { ACCOUNT_SECURITY_PAGE } from '@cortex/app-host/contract';
import { AlertTriangle, Loader2 } from 'lucide-react';
import * as React from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { useAppChatBridge, type AppChatBridgeHost } from './app-chat-bridge.ts';
import { appPath, legacyRouteOf, pagePath, pathOfRoute, routeOfPath } from './paths.ts';
import {
  appFetch,
  appInfo,
  getAccessToken,
  keepRuntimeSession,
  moduleRuns,
  runtimeSession,
  signOut,
} from './session.ts';

const LOAD_TIMEOUT_MS = 45_000;

const APP_REACH_HEADER = 'x-cortex-app';
const q = encodeURIComponent;

/** The app's headless chat in this shell (ADR 0021 amendment of 2026-10-08): the app's own chat service. */
const CHAT_BRIDGE: AppChatBridgeHost = {
  apiBaseUrl: '/',
  assistantBaseUrl: '/assistant',
  getToken: () => getAccessToken(),
  companions: async (_slug, app) => {
    const r = await appFetch(`/api/structure/apps/${q(app)}/companions`, {
      headers: { [APP_REACH_HEADER]: app },
    });
    return r.ok ? ((await r.json()) as Awaited<ReturnType<AppChatBridgeHost['companions']>>) : null;
  },
  reportPath: (_slug, id) => `${pagePath('chat-report')}?id=${q(id)}`,
};

/** The app's deep link from the page's query (`?<hash>`). */
export function deepLinkOf(search: string): string {
  return search.startsWith('?') ? decodeURIComponent(search.slice(1)) : '';
}

export function AppView() {
  const location = useLocation();
  const navigate = useNavigate();
  const info = appInfo();
  const slug = info.project.slug;
  const home = info.home ?? info.apps[0]?.key ?? null;
  // The home app at its own addresses (any path the shell does not name); at /apps/<app> that app (the home app's own
  // address is its route's path, never /apps/<home>).
  const routed = useParams().viewName;
  const atHome = routed === undefined;
  const viewName = routed ?? home ?? '';
  const app = info.apps.find((a) => a.key === viewName) ?? null;
  const atOwnAddress = !(routed && routed === home);
  const [session, setSession] = React.useState<'pending' | 'ok' | 'failed'>('pending');
  const [ready, setReady] = React.useState(false);
  const [failed, setFailed] = React.useState<string | null>(null);
  const [attempt, setAttempt] = React.useState(0);
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);

  // Signed in to the runtime (its session cookie), so the app opens signed in; each new bearer follows.
  React.useEffect(() => {
    let alive = true;
    setSession('pending');
    runtimeSession().then(
      () => alive && setSession('ok'),
      () => alive && setSession('failed'),
    );
    const stop = keepRuntimeSession();
    return () => {
      alive = false;
      stop();
    };
  }, [attempt]);

  // The home app's route is the address's path (an earlier /?<route> leads there); another app's is in the query.
  const legacy = atHome ? legacyRouteOf(location.pathname, location.search) : null;
  const deepLink =
    legacy ?? (atHome ? routeOfPath(location.pathname, location.search) : deepLinkOf(location.search));
  React.useEffect(() => {
    if (legacy !== null) navigate(pathOfRoute(legacy), { replace: true });
  }, [legacy, navigate]);
  const startLink = React.useRef<{ key: string | null; link: string }>({ key: null, link: '' });
  if (startLink.current.key !== (app?.key ?? null))
    startLink.current = { key: app?.key ?? null, link: deepLink };
  const runtimeUrl = app ? `${window.location.origin}/${encodeURIComponent(app.key)}/` : null;
  // The app's own chat UI reaches the chat through this shell (when the app pins the chat SDK).
  useAppChatBridge(iframeRef, {
    slug,
    app: app && moduleRuns('chat') ? app.key : null,
    runtimeUrl,
    host: CHAT_BRIDGE,
  });
  const src =
    runtimeUrl && session !== 'pending'
      ? `${runtimeUrl}${startLink.current.link ? `#${startLink.current.link}` : ''}`
      : null;

  React.useEffect(() => {
    setReady(false);
    setFailed(null);
    if (!src) return;
    const t = setTimeout(() => setFailed('timeout'), LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [src, attempt]);

  const lastRoute = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!runtimeUrl || !app) return;
    const origin = new URL(runtimeUrl).origin;
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== origin || e.source !== iframeRef.current?.contentWindow) return;
      const d = e.data as { source?: string; type?: string; hash?: string; message?: string };
      if (d?.source !== 'cortex-app') return;
      if (d.type === 'ready') setReady(true);
      else if (d.type === 'error') setFailed(d.message ?? 'The app failed to start');
      // The app's own Sign out (@cortex/app-sdk/client signOut): the identity provider's session ends.
      else if (d.type === 'sign-out') signOut();
      // The app's own account menu (@cortex/app-sdk/client openAccountSecurity): the person's sign-in security.
      else if (d.type === 'account-security' && appInfo().signIn?.security) navigate(ACCOUNT_SECURITY_PAGE);
      else if (d.type === 'route') {
        const raw = String(d.hash ?? '');
        // the home app's first page is one route, whether its hash is empty or `/`
        const hash = atHome && raw === '/' ? '' : raw;
        if (hash === lastRoute.current) return;
        lastRoute.current = hash;
        if (atHome) navigate(pathOfRoute(hash), { replace: true });
        else navigate(appPath(app.key, home) + (hash ? `?${hash}` : ''), { replace: true });
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [runtimeUrl, app?.key, home, navigate, atHome]);
  React.useEffect(() => {
    if (!runtimeUrl || !ready || deepLink === lastRoute.current) return;
    lastRoute.current = deepLink;
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { source: 'cortex-app-route', hash: deepLink },
        new URL(runtimeUrl).origin,
      );
    } catch {
      /* the frame navigated away */
    }
  }, [deepLink, runtimeUrl, ready]);

  React.useEffect(() => {
    if (app) document.title = `${app.title} · ${info.branding.name}`;
  }, [app, info.branding.name]);

  if (!atOwnAddress) return <Navigate to={{ pathname: '/', search: location.search }} replace />;
  if (!app)
    return (
      <div
        className="flex h-full items-center justify-center text-sm text-page-muted"
        data-testid="app-not-found"
      >
        This app has no app named {viewName}.
      </div>
    );

  return (
    <div className="flex h-full min-h-0" data-testid="apps-page" data-surface="app">
      <main className="relative flex-1 min-w-0 bg-page-bg">
        {src ? (
          <>
            <iframe
              key={`${app.key}#${attempt}`}
              ref={iframeRef}
              data-testid="app-view-iframe"
              src={src}
              title={app.title}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
            />
            {!ready && (
              <div
                style={{ position: 'absolute', inset: 0, zIndex: 10 }}
                className="flex flex-col items-center justify-center gap-3 bg-page-bg px-6 text-center"
                data-testid={failed ? 'app-failed' : 'app-loading'}
              >
                {failed ? (
                  <>
                    <AlertTriangle size={30} className="text-amber-600" />
                    <div className="text-base font-semibold text-page-title">
                      {failed === 'timeout' ? "This app didn't finish loading" : "You can't open this app"}
                    </div>
                    <div className="text-sm text-page-muted max-w-[460px]">
                      {failed === 'timeout'
                        ? 'It may still be starting up, or you may not have permission to run it. Try again, and contact the app’s owner if it keeps failing.'
                        : failed}
                    </div>
                    <button
                      type="button"
                      data-testid="app-try-again"
                      onClick={() => setAttempt((n) => n + 1)}
                      className="mt-1 h-9 px-4 rounded-[5px] border-0 cursor-pointer bg-nav-active text-white text-sm font-medium hover:bg-nav-active-dark transition-colors"
                    >
                      Try again
                    </button>
                  </>
                ) : (
                  <>
                    <Loader2 size={28} className="animate-spin text-nav-active" />
                    <div className="text-sm text-page-muted">Opening {app.title}…</div>
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          <div
            style={{ position: 'absolute', inset: 0 }}
            className="flex flex-col items-center justify-center gap-3 bg-page-bg"
            data-testid="app-loading"
          >
            <Loader2 size={28} className="animate-spin text-nav-active" />
            <div className="text-sm text-page-muted">Opening {app.title}…</div>
          </div>
        )}
      </main>
    </div>
  );
}
